import React, { useState, useEffect } from "react";
import {
  getProspects, createProspect, updateProspect, deleteProspect, enrichProspect, enrichMissingProspects, getEnrichStatus,
  scoreProspect, scoreAllProspects, getScoreStatus,
  syncProspectDolibarr, runAgent,
} from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Plus, Trash2, UploadCloud, Bot, X, Send, Pencil, Sparkles, Loader2, Gauge, ArrowDownWideNarrow } from "lucide-react";
import DuplicateHint from "./DuplicateHint";
import { log } from "../../lib/log";
import { joinAddress } from "../../lib/address";

const scoreCls = (n) => (n >= 70 ? "bg-emerald-600 text-white" : n >= 40 ? "bg-amber-400 text-neutral-900" : "bg-neutral-300 text-neutral-700");
const ScoreBadge = ({ p }) => (
  p.score === undefined || p.score === null ? null : (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${scoreCls(p.score)}`} title={p.score_raison || ""} data-testid={`prospect-score-${p.id}`}>
      <Gauge size={11} /> {p.score}/100 · {p.score_priorite}
    </span>
  )
);

const STATUS = ["nouveau", "contacte", "relance", "converti", "perdu"];
const statusColor = {
  nouveau: "bg-blue-100 text-blue-700",
  contacte: "bg-amber-100 text-amber-700",
  relance: "bg-purple-100 text-purple-700",
  converti: "bg-emerald-100 text-emerald-700",
  perdu: "bg-neutral-200 text-neutral-600",
};

const empty = { societe: "", contact: "", email: "", telephone: "", adresse: "", site: "", secteur: "", ville: "", source: "manuel", notes: "" };
const FIELDS = [["societe", "Société *"], ["contact", "Contact"], ["email", "Email"], ["telephone", "Téléphone"], ["adresse", "Adresse"], ["site", "Site web"], ["secteur", "Secteur"], ["ville", "Ville"]];

const ProspectsPanel = () => {
  const [prospects, setProspects] = useState([]);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [enriching, setEnriching] = useState(null);
  const [job, setJob] = useState(null);
  const [scoreJob, setScoreJob] = useState(null);
  const [scoring, setScoring] = useState(null);
  const [sortScore, setSortScore] = useState(false);
  const [agent, setAgent] = useState(null); // {prospect, subject, body, kind}
  const [agentLoading, setAgentLoading] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    const res = await getProspects();
    setProspects(res.data);
  };
   
  useEffect(() => { load(); }, []);

  const add = async (e, force = false) => {
    e?.preventDefault?.();
    try {
      await createProspect({ ...form, force });
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.duplicate) {
        const who = [...(d.matches || []).map((m) => `tiers Dolibarr ${m.name} (#${m.id})`), ...(d.prospects || []).map((p) => `prospect ${p.societe}`)].join(", ");
        if (window.confirm(`${d.message}.\nDéjà présent : ${who}.\n\nCréer quand même ?`)) return add(null, true);
        return;
      }
      return toast({ title: "Création impossible", description: typeof d === "string" ? d : "Erreur", variant: "destructive" });
    }
    setForm(empty);
    setShowForm(false);
    load();
    toast({ title: "Prospect ajouté ✓" });
  };

  const refreshJob = async () => {
    try { const r = await getEnrichStatus(); setJob(r.data); return r.data; } catch (err) { log.error("enrich-status:", err); return null; }
  };
   
  useEffect(() => { refreshJob(); }, []);
  useEffect(() => {
    if (!job?.running) return undefined;
    const t = setInterval(async () => {
      const st = await refreshJob();
      if (st && !st.running) { load(); toast({ title: "Enrichissement terminé ✓", description: `${st.found}/${st.total} prospect(s) avec un contact trouvé.` }); }
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.running]);

  const refreshScoreJob = async () => {
    try { const r = await getScoreStatus(); setScoreJob(r.data); return r.data; } catch (err) { log.error("score-status:", err); return null; }
  };
   
  useEffect(() => { refreshScoreJob(); }, []);
  useEffect(() => {
    if (!scoreJob?.running) return undefined;
    const t = setInterval(async () => {
      const st = await refreshScoreJob();
      if (st && !st.running) { load(); toast({ title: "Scoring terminé ✓", description: `${st.total} prospect(s) évalué(s).` }); }
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoreJob?.running]);

  const scoreOne = async (p) => {
    setScoring(p.id);
    try {
      const r = await scoreProspect(p.id);
      toast({ title: `Score ${r.data.score}/100 · priorité ${r.data.priorite}`, description: r.data.action });
      load();
    } catch (err) {
      toast({ title: "Scoring impossible", description: err?.response?.data?.detail || "Erreur IA", variant: "destructive" });
    } finally { setScoring(null); }
  };

  const scoreAll = async () => {
    try {
      const r = await scoreAllProspects(true);
      if (!r.data.started) return toast({ title: r.data.message || "Rien à scorer" });
      toast({ title: "Scoring lancé", description: `${r.data.total} prospect(s) en cours d'évaluation…` });
      refreshScoreJob();
    } catch (err) {
      toast({ title: "Impossible", description: err?.response?.data?.detail || "Erreur", variant: "destructive" });
    }
  };

  const enrichOne = async (p) => {
    setEnriching(p.id);
    try {
      const r = await enrichProspect(p.id);
      const filled = r.data.filled.filter((k) => k !== "notes");
      toast({ title: filled.length ? "Fiche mise à jour par l'IA ✓" : "Rien de nouveau trouvé", description: filled.length ? `Champs complétés : ${filled.join(", ")}` : "Aucune information supplémentaire sur le web." });
      load();
    } catch (err) {
      toast({ title: "Mise à jour impossible", description: err?.response?.data?.detail || "Erreur IA", variant: "destructive" });
    } finally { setEnriching(null); }
  };

  const enrichMissing = async () => {
    try {
      const r = await enrichMissingProspects(10);
      if (!r.data.started) return toast({ title: r.data.message || "Rien à enrichir" });
      toast({ title: "Enrichissement lancé", description: `${r.data.total} prospect(s) sans contact en cours de recherche web…` });
      refreshJob();
    } catch (err) {
      toast({ title: "Impossible", description: err?.response?.data?.detail || "Erreur", variant: "destructive" });
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!(editing.email || editing.telephone)) {
      return toast({ title: "Contact requis", description: "Renseignez au moins un email ou un téléphone.", variant: "destructive" });
    }
    try {
      const { id, societe, contact, email, telephone, adresse, site, secteur, ville, notes } = editing;
      const res = await updateProspect(id, { societe, contact, email, telephone, adresse, site, secteur, ville, notes });
      setEditing(null);
      load();
      toast({ title: "Prospect modifié ✓", description: res.data.dolibarr_socid ? (res.data.dolibarr_error ? "⚠️ Dolibarr non synchronisé" : "Tiers Dolibarr mis à jour") : undefined });
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Modification impossible", variant: "destructive" });
    }
  };

  const setStatus = async (id, status) => {
    await updateProspect(id, { status });
    setProspects((p) => p.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  const remove = async (id) => {
    await deleteProspect(id);
    load();
  };

  const sync = async (id) => {
    const res = await syncProspectDolibarr(id);
    if (res.data.socid) {
      toast({ title: "Synchronisé avec Dolibarr ✓", description: `Tiers #${res.data.socid}` });
      load();
    } else {
      toast({ title: "Erreur Dolibarr", description: res.data.error || "Échec", variant: "destructive" });
    }
  };

  const openAgent = async (prospect, kind) => {
    setAgent({ prospect, subject: "", body: "", kind });
    setAgentLoading(true);
    try {
      const res = await runAgent({ prospect_id: prospect.id, kind, send: false });
      setAgent({ prospect, subject: res.data.subject, body: res.data.body, kind });
    } catch (e) {
      toast({ title: "Erreur agent IA", variant: "destructive" });
      setAgent(null);
    } finally {
      setAgentLoading(false);
    }
  };

  const sendAgent = async () => {
    if (!agent?.prospect?.email) {
      toast({ title: "Pas d'email", description: "Ce prospect n'a pas d'adresse email.", variant: "destructive" });
      return;
    }
    setAgentLoading(true);
    try {
      const res = await runAgent({ prospect_id: agent.prospect.id, kind: agent.kind, send: true });
      if (res.data.sent?.ok) {
        toast({ title: "Email envoyé ✓", description: agent.prospect.email });
        setAgent(null);
        load();
      } else {
        toast({ title: "Envoi échoué", description: res.data.sent?.error || "", variant: "destructive" });
      }
    } finally {
      setAgentLoading(false);
    }
  };

  const inputCls = "w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand";

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-bold text-xl text-neutral-900">Prospects (CRM)</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => setSortScore(!sortScore)} data-testid="prospects-sort-score" className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-md border ${sortScore ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-neutral-700"}`}>
            <ArrowDownWideNarrow size={15} /> Trier par score
          </button>
          <button onClick={scoreAll} disabled={scoreJob?.running || !scoreJob?.unscored} data-testid="prospects-score-all-btn"
            className="flex items-center gap-2 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60">
            {scoreJob?.running ? <Loader2 size={15} className="animate-spin" /> : <Gauge size={15} />}
            {scoreJob?.running ? `Scoring ${scoreJob.done}/${scoreJob.total}…` : `Scorer les prospects${scoreJob?.unscored ? ` (${scoreJob.unscored})` : ""}`}
          </button>
          <button onClick={enrichMissing} disabled={job?.running || !job?.missing} data-testid="prospects-enrich-missing-btn"
            className="flex items-center gap-2 border border-violet-300 text-violet-700 hover:bg-violet-50 text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60">
            {job?.running ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
            {job?.running ? `Enrichissement ${job.done}/${job.total}…` : `Enrichir les prospects sans contact${job?.missing ? ` (${job.missing})` : ""}`}
          </button>
          <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-4 py-2 rounded-md">
            <Plus size={16} /> Nouveau prospect
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={add} className="bg-white border border-neutral-200 rounded-xl p-5 mb-5 grid sm:grid-cols-2 gap-3">
          <input required placeholder="Société *" className={inputCls} value={form.societe} onChange={(e) => setForm({ ...form, societe: e.target.value })} />
          <input placeholder="Contact" className={inputCls} value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
          <input placeholder="Email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="prospect-email-input" />
          <DuplicateHint email={form.email} name={form.societe} />
          <input placeholder="Téléphone" className={inputCls} value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
          <input placeholder="Secteur" className={inputCls} value={form.secteur} onChange={(e) => setForm({ ...form, secteur: e.target.value })} />
          <input placeholder="Ville" className={inputCls} value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} />
          <textarea placeholder="Notes" className={`${inputCls} sm:col-span-2`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <button type="submit" className="sm:col-span-2 bg-brand hover:bg-brand-dark text-white font-semibold py-2.5 rounded-md">Ajouter</button>
        </form>
      )}

      {prospects.length === 0 ? (
        <p className="text-neutral-500">Aucun prospect. Ajoutez-en un pour lancer la prospection.</p>
      ) : (
        <div className="space-y-3">
          {(sortScore ? [...prospects].sort((a, b) => (b.score ?? -1) - (a.score ?? -1)) : prospects).map((p) => (
            <div key={p.id} className="bg-white border border-neutral-200 rounded-xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-neutral-900">{p.societe}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${statusColor[p.status] || ""}`}>{p.status}</span>
                    {p.dolibarr_socid && <span className="text-xs text-emerald-600">✓ Dolibarr #{p.dolibarr_socid}</span>}
                    <ScoreBadge p={p} />
                  </div>
                  {p.score_action && <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded px-2 py-1 mt-1.5 inline-block" data-testid={`prospect-action-${p.id}`}>→ {p.score_action}</p>}
                  <p className="text-sm text-neutral-600 mt-1">{p.contact ? `${p.contact} · ` : ""}{p.email || "—"} · {p.telephone || "—"}</p>
                  <p className="text-sm text-neutral-500">{p.secteur} · {joinAddress(p.adresse, p.ville)} · source: {p.source}{p.site ? ` · ${p.site}` : ""}{p.ai_enriched_at ? ` · IA ${new Date(p.ai_enriched_at).toLocaleDateString("fr-FR")}` : ""}</p>
                  {!(p.email || p.telephone) && <p className="text-xs text-amber-600 font-semibold mt-0.5">⚠️ Aucun contact : complétez l'email ou le téléphone</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={p.status} onChange={(e) => setStatus(p.id, e.target.value)} className="border border-neutral-300 rounded-md px-2 py-1.5 text-sm">
                    {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => openAgent(p, "prospection")} className="flex items-center gap-1 bg-brand/10 text-brand text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-brand/20">
                    <Bot size={14} /> Prospecter
                  </button>
                  <button onClick={() => openAgent(p, "relance")} className="flex items-center gap-1 bg-purple-50 text-purple-700 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-purple-100">
                    <Bot size={14} /> Relance
                  </button>
                  <button onClick={() => scoreOne(p)} disabled={scoring === p.id} title="Évaluer le potentiel commercial" data-testid={`prospect-score-btn-${p.id}`}
                    className="flex items-center gap-1 border border-emerald-200 text-emerald-700 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-emerald-50 disabled:opacity-60">
                    {scoring === p.id ? <Loader2 size={14} className="animate-spin" /> : <Gauge size={14} />} {p.score !== undefined ? "Re-scorer" : "Scorer"}
                  </button>
                  <button onClick={() => enrichOne(p)} disabled={enriching === p.id} title="Mettre à jour la fiche via recherche web IA" data-testid={`prospect-enrich-${p.id}`}
                    className="flex items-center gap-1 border border-violet-200 text-violet-700 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-violet-50 disabled:opacity-60">
                    {enriching === p.id ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Mise à jour IA
                  </button>
                  <button onClick={() => setEditing({ ...empty, ...p })} title="Modifier" data-testid={`prospect-edit-${p.id}`} className="text-neutral-500 hover:text-brand p-1.5">
                    <Pencil size={17} />
                  </button>
                  <button onClick={() => sync(p.id)} title="Envoyer vers Dolibarr" className="text-neutral-500 hover:text-brand p-1.5">
                    <UploadCloud size={17} />
                  </button>
                  <button onClick={() => remove(p.id)} className="text-neutral-400 hover:text-red-500 p-1.5">
                    <Trash2 size={17} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="prospect-edit-modal">
          <form onSubmit={saveEdit} className="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Pencil size={18} className="text-brand" /> Modifier le prospect</h3>
              <button type="button" onClick={() => setEditing(null)} className="text-neutral-400 hover:text-neutral-700"><X size={20} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS.map(([k, label]) => (
                <div key={k}>
                  <label className="block text-xs font-semibold text-neutral-600 mb-1">{label}</label>
                  <input required={k === "societe"} className={inputCls} value={editing[k] || ""} data-testid={`prospect-edit-${k}`} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-neutral-600 mb-1">Notes</label>
                <textarea rows={3} className={inputCls} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>
            {editing.dolibarr_socid && <p className="text-xs text-neutral-500 mt-3">Le tiers Dolibarr #{editing.dolibarr_socid} sera mis à jour automatiquement.</p>}
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-sm font-semibold text-neutral-600 border border-neutral-300 rounded-md">Annuler</button>
              <button type="submit" data-testid="prospect-edit-save" className="bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-5 py-2 rounded-md">Enregistrer</button>
            </div>
          </form>
        </div>
      )}

      {agent && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                <Bot size={20} className="text-brand" /> Agent IA — {agent.kind === "relance" ? "Relance" : "Prospection"}
              </h3>
              <button onClick={() => setAgent(null)} className="text-neutral-400 hover:text-neutral-700"><X size={20} /></button>
            </div>
            <p className="text-sm text-neutral-500 mb-3">Destinataire : {agent.prospect.societe} ({agent.prospect.email || "pas d'email"})</p>
            {agentLoading && !agent.body ? (
              <p className="text-neutral-500 py-8 text-center">L'agent IA rédige l'email...</p>
            ) : (
              <>
                <label className="block text-xs font-semibold text-neutral-600 mb-1">Sujet</label>
                <input className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm mb-3" value={agent.subject} onChange={(e) => setAgent({ ...agent, subject: e.target.value })} />
                <label className="block text-xs font-semibold text-neutral-600 mb-1">Corps de l'email</label>
                <textarea rows={9} className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm mb-4" value={agent.body} onChange={(e) => setAgent({ ...agent, body: e.target.value })} />
                <div className="flex justify-end gap-3">
                  <button onClick={() => setAgent(null)} className="px-4 py-2 text-sm font-semibold text-neutral-600 border border-neutral-300 rounded-md">Annuler</button>
                  <button onClick={sendAgent} disabled={agentLoading} className="flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60">
                    <Send size={15} /> Envoyer l'email
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProspectsPanel;
